import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { IonButton, IonSpinner } from '@ionic/angular/standalone';
import { EMPTY, Subject, catchError, filter, map, of, switchMap, take, takeUntil, tap, throwError, timer } from 'rxjs';
import {
  INotificationRecording,
  VideoRecordService
} from '../../../core/services/video.records.service';
import { LoggerService } from '../../../core/services/utils/logger.service';

@Component({
  selector: 'app-alert-video-dialog',
  standalone: true,
  templateUrl: './alert-video-dialog.component.html',
  styleUrls: ['./alert-video-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    IonSpinner,
    IonButton
  ]
})
export class AlertVideoDialogComponent {
  public state: AlertVideoDialogState = 'loading';
  public primaryRecording: INotificationRecording | null = null;
  public videoUrl: string | null = null;
  public errorMessage: string = '';

  private destroy$ = new Subject<void>();
  private activeLoadStop$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IAlertVideoDialogData,
    private videoRecordService: VideoRecordService,
    private loggerService: LoggerService
  ) { }

  ngOnInit() {
    this.loadVideo();
  }

  ngOnDestroy() {
    this.stopActiveFlow();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadVideo() {
    this.stopActiveFlow();
    this.activeLoadStop$ = new Subject<void>();

    this.state = 'loading';
    this.primaryRecording = null;
    this.videoUrl = null;
    this.errorMessage = '';

    this.videoRecordService.getPrimaryNotificationAlertVideo$(this.data.notificationId).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.activeLoadStop$),
      switchMap((recording) => {
        if (!recording) {
          this.state = 'noVideo';
          return EMPTY;
        }

        this.primaryRecording = recording;
        this.state = 'preparing';

        return this.videoRecordService.demandVideo$(this.data.notificationId, recording.recordingId).pipe(
          switchMap(() => this.pollVideoAccess$(recording.recordingId))
        );
      }),
      tap((videoUrl) => {
        this.videoUrl = videoUrl;
        this.state = 'ready';
      }),
      catchError((error) => {
        const errorMessage = this.getErrorMessage(error);
        this.loggerService.error(`Error while loading alert video for notification ${this.data.notificationId}: ${errorMessage}`);
        this.state = 'error';
        this.errorMessage = errorMessage;
        return EMPTY;
      })
    ).subscribe();
  }

  private pollVideoAccess$(recordingId: string) {
    return timer(0, 3000).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.activeLoadStop$),
      switchMap(() =>
        this.videoRecordService.requestVideoUrl$(this.data.notificationId, recordingId).pipe(
          map((response) => this.videoRecordService.extractVideoUrl(response)),
          catchError((error) => {
            if (this.videoRecordService.isVideoNotReadyError(error)) {
              this.state = 'preparing';
              return of(null);
            }

            return throwError(() => error);
          })
        )
      ),
      filter((videoUrl): videoUrl is string => typeof videoUrl === 'string' && videoUrl.trim().length > 0),
      take(1)
    );
  }

  private stopActiveFlow() {
    this.activeLoadStop$.next();
    this.activeLoadStop$.complete();
  }

  private getErrorMessage(error: unknown): string {
    if (this.videoRecordService.isVideoNotReadyError(error)) {
      return 'Il video non e ancora pronto per la riproduzione.';
    }

    const backendMessage =
      typeof (error as { error?: string | { message?: string } })?.error === 'string'
        ? (error as { error: string }).error
        : (error as { error?: { message?: string }, message?: string })?.error?.message ||
        (error as { message?: string })?.message;

    if (backendMessage) {
      return backendMessage;
    }

    return 'Si e verificato un errore durante il caricamento del video.';
  }
}

export type AlertVideoDialogState = 'loading' | 'noVideo' | 'preparing' | 'ready' | 'error';

export interface IAlertVideoDialogData {
  notificationId: string;
  title: string;
}
